import { Badge } from "@akaike/ui";

import type { TierCount } from "../api/generated/model";
import { platforms } from "../api/platforms";

// Tier colours are served by the platform contract (/v1/platforms -> TierMeta.color): the
// store-detail map, per-location tier pills, and the Stores-list role pill all read the
// same colour for a token. Colours are consistent across platforms, so merging every
// platform's tiers reproduces one global token -> colour map (the former hand-maintained
// TIER_COLORS). Rebuilt from the boot-populated cache on each lookup (cheap). Anything the
// contract doesn't carry gets a stable hashed colour.
function tierColorFromContract(token: string): string | undefined {
  for (const p of platforms()) {
    const t = p.tiers.find((x) => x.token === token);
    if (t) return t.color;
  }
  return undefined;
}
// Tier chips carry a colour the platform contract serves, not one of the design's
// tones, so they stay an outline Badge with the colour supplied inline. Only the
// geometry differs from a status chip: denser, and the labels arrive lowercased.
export const TIER_CHIP =
  "gap-[5px] px-2 py-px text-[10.5px]/[1.55] capitalize";

// Legend / ordering preference: fastest/primary first; unknown tiers sort after.
export const TIER_ORDER = [
  "express",
  "fast",
  "primary",
  "instant",
  "unicorn",
  "regular",
  "longtail",
  "secondary",
  "super_longtail",
  "invalid",
];
const HASH_PALETTE = ["#1f6feb", "#9a6700", "#bc4c00", "#1b7c83", "#a40e26", "#6639ba"];
const UNKNOWN_COLOR = "#8b949e";

// Normalize a platform's tier string to a short token: strip the verbose enum
// boilerplate Zepto/Instamart use (PRIMARY_STORE, SERVICEABILITY_TYPE_FAST_SERVICEABLE,
// PRIORITY_PRIMARY -> primary / fast). Idempotent: Blinkit's already-short order types
// and the engine's pre-canonicalized cell tags pass through unchanged.
export function canonTier(raw: string): string {
  let t = (raw || "").trim().toUpperCase();
  if (!t) return "";
  if (t.startsWith("SERVICEABILITY_TYPE_")) t = t.slice("SERVICEABILITY_TYPE_".length).replaceAll("_SERVICEABLE", "");
  if (t.startsWith("PRIORITY_")) t = t.slice("PRIORITY_".length);
  if (t.endsWith("_STORE")) t = t.slice(0, -"_STORE".length);
  return t.toLowerCase();
}

export function tierColor(tier: string): string {
  const t = canonTier(tier);
  if (!t) return UNKNOWN_COLOR;
  const fromContract = tierColorFromContract(t);
  if (fromContract) return fromContract;
  // The "invalid" service-class marker is not a delivery lane, so it isn't in the
  // contract; keep its neutral grey rather than a hashed colour.
  if (t === "invalid") return UNKNOWN_COLOR;
  let h = 0;
  for (let i = 0; i < t.length; i++) h = (h * 31 + t.charCodeAt(i)) >>> 0;
  return HASH_PALETTE[h % HASH_PALETTE.length];
}

export function tierLabel(tier: string): string {
  const t = canonTier(tier);
  return t ? t.replace(/_/g, " ") : "untiered";
}

// Sequential green-to-red scale for the store-detail map's "overlap" view, keyed on how many
// stores serve a cell (this store included). 1 = exclusive (this store alone owns the cell)
// reads green/good; the more stores pile onto a cell the more contested (redder) it is. 4+
// collapse into one bucket - beyond that the exact count matters less than "heavily shared".
const OVERLAP_COLORS = ["#1a7f37", "#bf8700", "#bc4c00", "#cf222e"]; // 1, 2, 3, 4+
export const OVERLAP_MAX_BUCKET = 4;

export function overlapColor(servingCount: number): string {
  const n = Math.max(1, Math.min(OVERLAP_MAX_BUCKET, servingCount));
  return OVERLAP_COLORS[n - 1];
}

export function overlapLabel(servingCount: number): string {
  if (servingCount <= 1) return "exclusive (1 store)";
  if (servingCount >= OVERLAP_MAX_BUCKET) return `${OVERLAP_MAX_BUCKET}+ stores`;
  return `${servingCount} stores`;
}

// A store's per-location tier mix as colour-coded pills (already sorted most-served-first
// by the backend), each tagged with its probe-point count. Used on the store-detail page
// for the location-varying service class. Falls back to a plain string (the old scalar
// tier) when there's no per-cell tier data, or an em dash when there's none.
export function TierPills({ tiers, fallback }: { tiers?: TierCount[]; fallback?: string }) {
  if (!tiers || tiers.length === 0) {
    return <span className="faint">{fallback || "—"}</span>;
  }
  return (
    <span className="inline-flex flex-wrap gap-1">
      {tiers.map((t) => {
        const c = tierColor(t.tier);
        return (
          <Badge
            key={t.tier || "untiered"}
            variant="outline"
            className={TIER_CHIP}
            style={{ color: c, borderColor: c, background: `${c}1f` }}
          >
            {tierLabel(t.tier)}
            <span className="font-normal tabular-nums opacity-75">{t.count}</span>
          </Badge>
        );
      })}
    </span>
  );
}

// A single colour-coded pill for a store's stable role (primary/secondary store: Zepto
// storeConstruct / Instamart priority), shown in the Stores list. No count: this is the
// store's fixed role, not a per-location mix. Empty -> em dash (not "untiered").
export function RolePill({ tier }: { tier: string }) {
  const t = canonTier(tier);
  if (!t) return <span className="faint">—</span>;
  const c = tierColor(t);
  return (
    <Badge variant="outline" className={TIER_CHIP} style={{ color: c, borderColor: c, background: `${c}1f` }}>
      {tierLabel(t)}
    </Badge>
  );
}

// The store's coverage-census probe role (anchors.py AnchorKind): where its routing probe
// point sits in the overlap ladder. exclusive = sole server of its cell (green); primary =
// shares the cell but wins the route (blue); least_overlap = never wins, always shadowed
// (amber); direct = directly-addressable platform, no ladder (Zepto, purple). Empty -> em
// dash (legacy pre-census rows).
const ANCHOR_KIND_COLORS: Record<string, string> = {
  exclusive: "#1a7f37",
  primary: "#0969da",
  least_overlap: "#bf8700",
  direct: "#8250df",
};

export function AnchorKindPill({ kind }: { kind?: string }) {
  const k = (kind || "").trim().toLowerCase();
  if (!k) return <span className="faint">—</span>;
  const c = ANCHOR_KIND_COLORS[k] ?? UNKNOWN_COLOR;
  return (
    <Badge variant="outline" className={TIER_CHIP} style={{ color: c, borderColor: c, background: `${c}1f` }}>
      {k.replace(/_/g, " ")}
    </Badge>
  );
}
